/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';
import { jsx } from '@compiled/react';
import { usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import { graphql } from 'relay-runtime';
import { JiraInvitePeopleModalContextProvider } from '@jira/project-settings__invite-people-modal-context/src/JiraInvitePeopleModalContextProvider.tsx';
import { cssMap } from '@atlaskit/css';
import { Box, Flex, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { PanelSplitter, SideNav, SideNavFooter } from '@atlaskit/navigation-system';
import { SideNavPanelSplitter } from '@atlaskit/navigation-system/layout/side-nav';
import Button from '@atlaskit/button/new';
import { componentWithFG } from '@atlassian/jira-feature-gate-component/src/index.tsx';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { useLeftSidebarState } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/consumers/left-sidebar-controller/index.tsx';
import { useLayoutStoreActions } from '@atlassian/jira-layout-controller/src/controllers/layout-controller/store/index.tsx';
import { sidebarNav4UiStateResource } from '@atlassian/jira-navigation-apps-resources/src/services/sidebar-nav4/index.tsx';
import { SidebarNav4ContextProvider } from '@atlassian/jira-navigation-apps-sidebar-nav4-context/src/controllers/sidebar-context/index.tsx';
import {
	LEFT_SIDEBAR_EXPANDED_MIN_WIDTH,
	IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT,
} from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-common-core/src/common/constants/index.tsx';
import { PlaceholderNew } from '@atlassian/jira-placeholder/src/index.tsx';
import { useRelayResource } from '@atlassian/jira-relay-utils/src/services/resources/index.tsx';
import { SidebarFeedbackButton } from '@atlassian/jira-sidebar-nav4-feedback-button/src/ui/index.tsx';
import { useIsAnonymous } from '@atlassian/jira-tenant-context-controller/src/components/is-anonymous/index.tsx';
import UFOSegment from '@atlassian/jira-ufo-segment/src/index.tsx';
import { AsyncNudgeBundledUsersFlagContainer } from '@atlassian/jira-nudge-bundled-users-to-join-connie/src/async.tsx';
import { AsyncNudgeInactiveConfluenceUserFlagContainer } from '@atlassian/jira-nudge-inactive-confluence-user/src/ui/async.tsx';
import { SidebarEntryPointSelector } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebar-selector/src/ui/entrypoint/index.tsx';
import { Nav4JiraSideNavRecommendation } from '@atlassian/jira-side-nav-recommendation/src/index.tsx';
import { NavPlatformSidebar } from '@atlassian/jira-nav-platform-integration-sidebar/src/ui/index.tsx';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { isLegacyAuiBlanketClosed } from '@atlassian/jira-common-components-keyboard-shortcuts/src/isLegacyAuiBlanketClosed.tsx';
import { useInvitePeopleModal } from '@atlassian/jira-navigation-apps-sidebar-nav4-sidebars-content-teams/src/ui/use-invite-people-modal.tsx';
import type { sidebarSelectorQuery } from './__generated__/sidebarSelectorQuery.graphql';
import { messages } from './messages.tsx';

const footerStyles = cssMap({
	root: {
		backgroundColor: token('elevation.surface.raised'),
	},
});

/**
 * This test ID is used in pollinator tests. Do not change lightly.
 */
const TEST_ID = 'page-layout.sidebar';

// TODO: use importCond() here
export const Sidebar = componentWithFG('navx-global-side-nav', NavPlatformSidebar, SidebarNav4);

function SidebarNav4() {
	const isAnonymous = useIsAnonymous();

	// TODO: Ask Matt why we use the Relay resource here, instead of inside SidebarInternal.
	const { queryReference } = useRelayResource(sidebarNav4UiStateResource);

	if (isAnonymous) {
		return (
			<UFOSegment name="sidebar.anonymous">
				<PlaceholderNew name="nav4-ignore.sidebar.anonymous">
					<SidebarInternalAnonymous />
				</PlaceholderNew>
			</UFOSegment>
		);
	}
	return (
		<UFOSegment name="nav4.sidebar">
			<PlaceholderNew name="nav4-ignore.sidebar">
				{queryReference ? <SidebarInternal queryReference={queryReference} /> : null}
			</PlaceholderNew>
		</UFOSegment>
	);
}

function SidebarInternalAnonymous() {
	const { formatMessage } = useIntl();

	const defaultCollapsed = false;
	const defaultWidth = 340;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: false });
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				setLeftSidebarState({ isCollapsed: true });
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ finalWidth }: { finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });
		},
		[setLeftSidebarState],
	);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={fg('navx-full-height-sidebar') ? undefined : defaultCollapsed}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
			>
				<SidebarEntryPointSelector />
				{fg('navx-full-height-sidebar') ? (
					<SideNavPanelSplitter
						label={formatMessage(messages.sideBarPanelSplitterLabel)}
						tooltipContent={formatMessage(messages.sideBarPanelSplitterTooltipContent)}
						onResizeEnd={onResizeEnd}
						testId="page-container-v2.common.ui.sidebar.panel-splitter"
					/>
				) : (
					<PanelSplitter
						label={formatMessage(messages.sideBarPanelSplitterLabel)}
						onResizeEnd={onResizeEnd}
						testId="page-container-v2.common.ui.sidebar.panel-splitter"
					/>
				)}
			</SideNav>
		</SidebarNav4ContextProvider>
	);
}

function InviteButton() {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const invitePeopleModalTrigger = useInvitePeopleModal();

	const openModalDialog = useCallback(() => {
		fireUIAnalytics(
			createAnalyticsEvent({
				action: 'clicked',
				actionSubject: 'button',
				actionSubjectId: 'SideNavFooterInviteButton',
				source: 'SideNavFooter',
			}),
		);
		invitePeopleModalTrigger();
	}, [createAnalyticsEvent, invitePeopleModalTrigger]);

	return (
		<Button
			shouldFitContainer
			onClick={openModalDialog}
			testId="page-container-v2.common.ui.sidebar.footer-invite-button"
		>
			{formatMessage(messages.globalInviteButtonLabel)}
		</Button>
	);
}

function SidebarInternal({
	queryReference,
}: {
	queryReference: PreloadedQuery<sidebarSelectorQuery>;
}) {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const data = usePreloadedQuery<sidebarSelectorQuery>(
		// eslint-disable-next-line jira/graphql/restrict-graphql-in-unnamed-files
		graphql`
			query sidebarSelectorQuery($cloudId: ID!, $isSkipped: Boolean!) {
				jira @skip(if: $isSkipped) {
					navigationUIState(cloudId: $cloudId) {
						isLeftSidebarCollapsed
						leftSidebarWidth
					}
				}
			}
		`,
		queryReference,
	);

	const { width: leftSidebarWidth, isCollapsed: isLeftSidebarCollapsed } = useLeftSidebarState();
	/*
		Backend may respond with an error with the reason as Navigation UI state user property does not exist
		under a scenario where a new user joins an existing Jira site.
		When this happens, UI would not have data coming through from sidebarSelectorQuery.
		So, default values are used in such a case.
	*/
	const defaultCollapsed = useMemo(
		() =>
			fg('blu-8363-archive-legacy-resource-navigationuistate')
				? (isLeftSidebarCollapsed ?? IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT)
				: (data?.jira?.navigationUIState?.isLeftSidebarCollapsed ??
					IS_LEFT_SIDEBAR_COLLAPSED_DEFAULT),
		[data?.jira?.navigationUIState?.isLeftSidebarCollapsed, isLeftSidebarCollapsed],
	);
	const defaultWidth = useMemo(
		() =>
			fg('blu-8363-archive-legacy-resource-navigationuistate')
				? (leftSidebarWidth ?? LEFT_SIDEBAR_EXPANDED_MIN_WIDTH)
				: (data?.jira?.navigationUIState?.leftSidebarWidth ?? LEFT_SIDEBAR_EXPANDED_MIN_WIDTH),
		[data?.jira?.navigationUIState?.leftSidebarWidth, leftSidebarWidth],
	);

	const { isCollapsed: currentCollapsed, hasUserPreference } = useLeftSidebarState();

	const effectiveCollapsed = hasUserPreference ? currentCollapsed : defaultCollapsed;

	const [, { setLeftSidebarState }] = useLayoutStoreActions();
	const onExpand = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				if (fg('jira-full-screen')) {
					setLeftSidebarState({ hasUserPreference: true, isCollapsed: false });
				} else {
					setLeftSidebarState({ isCollapsed: false });
				}
			}
		},
		[setLeftSidebarState],
	);

	const onCollapse = useCallback(
		({ screen }: { screen: 'desktop' | 'mobile' }) => {
			if (screen === 'desktop') {
				if (fg('jira-full-screen')) {
					setLeftSidebarState({ hasUserPreference: true, isCollapsed: true });
				} else {
					setLeftSidebarState({ isCollapsed: true });
				}
			}
		},
		[setLeftSidebarState],
	);

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			setLeftSidebarState({ width: finalWidth });

			fireUIAnalytics(
				createAnalyticsEvent({
					action: 'resized',
					actionSubject: 'sidebar',
				}),
				{
					initialWidth,
					finalWidth,
				},
			);
		},
		[createAnalyticsEvent, setLeftSidebarState],
	);

	const defaultCollapsedWithFG = useMemo(() => {
		if (fg('navx-full-height-sidebar')) {
			return undefined;
		}

		if (fg('jira-full-screen')) {
			return effectiveCollapsed;
		}

		return defaultCollapsed;
	}, [effectiveCollapsed, defaultCollapsed]);

	return (
		<SidebarNav4ContextProvider>
			<SideNav
				testId={TEST_ID}
				defaultCollapsed={defaultCollapsedWithFG}
				defaultWidth={defaultWidth}
				onExpand={onExpand}
				onCollapse={onCollapse}
				// Block the side nav toggle keyboard shortcut when a legacy AUI dialog is open
				canToggleWithShortcut={isLegacyAuiBlanketClosed}
			>
				<SidebarEntryPointSelector />
				<SideNavFooter xcss={footerStyles.root}>
					<Nav4JiraSideNavRecommendation />
					{expVal('jfind-add-invite-button-to-left-nav-experiment', 'enrolled', false) ? (
						<Flex gap="space.100">
							<Box xcss={globalInviteButtonStyles}>
								<JiraInvitePeopleModalContextProvider
									renderedInProjectContext={false}
									shouldDisplaySuccessModal={false}
								>
									<InviteButton />
								</JiraInvitePeopleModalContextProvider>
							</Box>
							<SidebarFeedbackButton />
						</Flex>
					) : (
						<SidebarFeedbackButton />
					)}
				</SideNavFooter>
				{fg('navx-full-height-sidebar') ? (
					<SideNavPanelSplitter
						label={formatMessage(messages.sideBarPanelSplitterLabel)}
						tooltipContent={formatMessage(messages.sideBarPanelSplitterTooltipContent)}
						onResizeEnd={onResizeEnd}
						testId="page-container-v2.common.ui.sidebar.panel-splitter"
					/>
				) : (
					<PanelSplitter
						label={formatMessage(messages.sideBarPanelSplitterLabel)}
						onResizeEnd={onResizeEnd}
						testId="page-container-v2.common.ui.sidebar.panel-splitter"
					/>
				)}
				<AsyncNudgeBundledUsersFlagContainer />
				{fg('jira-nudge-confluence-inactive-users-eligible') && (
					<AsyncNudgeInactiveConfluenceUserFlagContainer />
				)}
			</SideNav>
		</SidebarNav4ContextProvider>
	);
}

const globalInviteButtonStyles = xcss({
	flexGrow: 1,
});
